import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, Info, Remove, Save } from "@mui/icons-material";
import moment from "moment-timezone";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import CloseButton from "../../../../components/commonComponents/Buttons/CloseButton";
import Confirmation from "../../../../components/commonComponents/Confirmation";
import DateInputField from "../../../../components/commonComponents/TextInputs/DateInputs";
import TextInputField from "../../../../components/commonComponents/TextInputs/TextInputField";
import TimePickerField from "../../../../components/commonComponents/TextInputs/TimePickerField";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  StyledSwitch,
} from "../../../../components/commonComponents/StyledComponents/styled";
import { toast } from "react-toastify";
import { saveSpecialHours, updateSpecialHours, normalizeCloseTime, isInvertedRange } from "../../../../api_calls/infrastructure";
import SingleSelect from "../../../../components/commonComponents/Selectors/SingleSelect";

const SPECIAL_HOURS_TYPES = ["Open", "Open 24 Hours", "Closed"];

const DEFAULT_TIME_RANGE = () => ({ open: "08:00", close: "20:00" });
const DEFAULT_QUIET_RANGE = () => ({ start: "08:00", end: "20:00" });

const detectRangeOverlap = (ranges, idx, openKey, closeKey) => {
  const r = ranges[idx];
  if (!r || !r[openKey] || !r[closeKey]) return false;
  const rClose = normalizeCloseTime(r[closeKey]);
  return ranges.some((other, i) => {
    if (i === idx) return false;
    if (!other[openKey] || !other[closeKey]) return false;
    const otherClose = normalizeCloseTime(other[closeKey]);
    return !(rClose <= other[openKey] || r[openKey] >= otherClose);
  });
};

const TimeRangeBlock = ({ ranges, onChange, openKey = "open", closeKey = "close", showEmptyErrors }) => {
  const handleChangeField = (idx, field, val) => {
    onChange(ranges.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const handleAdd = () => {
    if (ranges.length >= 5) return;
    onChange([...ranges, openKey === "open" ? { open: "", close: "" } : { start: "", end: "" }]);
  };

  const handleRemove = (idx) => {
    onChange(ranges.filter((_, i) => i !== idx));
  };

  return (
    // Small vertical gap between stacked time ranges so multiple ranges read as
    // separate rows rather than butting together.
    <Stack spacing={0.75}>
      {ranges.map((r, idx) => {
        const hasOverlap = detectRangeOverlap(ranges, idx, openKey, closeKey);
        const inverted = isInvertedRange(r[openKey], r[closeKey]);
        const isFirst = idx === 0;
        return (
          <Box key={idx} sx={{ pt: isFirst && ranges.length > 1 ? 0.5 : 0 }}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <Box sx={{ width: 130 }}>
                <TimePickerField
                  elemId={openKey + "_" + idx}
                  label={isFirst ? (openKey === "open" ? "Open" : "Start") : ""}
                  defaultVal={r[openKey]}
                  onChangeCall={(val) => handleChangeField(idx, openKey, val)}
                />
              </Box>
              <Typography variant="body2" sx={{ pb: 1 }}>
                to
              </Typography>
              <Box sx={{ width: 130 }}>
                <TimePickerField
                  elemId={closeKey + "_" + idx}
                  label={isFirst ? (closeKey === "close" ? "Close" : "End") : ""}
                  defaultVal={r[closeKey]}
                  onChangeCall={(val) => handleChangeField(idx, closeKey, val)}
                />
              </Box>
              {/* + on the labeled row: offset by the label band so it sits on the
                  value line like − on unlabeled rows. */}
              {isFirst && ranges.length < 5 && (
                <Tooltip title="Add time range">
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignSelf: "flex-start", pt: "14px" }}
                  >
                    <IconButton size="small" onClick={handleAdd}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
              {!isFirst && (
                <Tooltip title="Remove time range">
                  <Box component="span" sx={{ display: "inline-flex", pb: 0.5 }}>
                    <IconButton size="small" onClick={() => handleRemove(idx)}>
                      <Remove fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
            </Stack>
            {inverted && (
              <FormHelperText error sx={{ ml: 0, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.3 }}>
                End time must be after start time
              </FormHelperText>
            )}
            {!inverted && hasOverlap && (
              <FormHelperText error sx={{ ml: 0, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.3 }}>
                Overlaps with another time range
              </FormHelperText>
            )}
            {/* Empty-field error surfaces only on save (showEmptyErrors), spans the
               whole row like the other errors, and clears once both times are set. */}
            {!inverted && !hasOverlap && showEmptyErrors && (!r[openKey] || !r[closeKey]) && (
              <FormHelperText error sx={{ ml: 0, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.3 }}>
                {openKey === "open" ? "Open and close time required" : "Start and end time required"}
              </FormHelperText>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const EMPTY_ENTRY = () => ({
  id: null,
  valid_on: null,
  name: "",
  type: "Open",
  open_hours: [DEFAULT_TIME_RANGE()],
  has_quiet_hours: false,
  quiet_hours: [DEFAULT_QUIET_RANGE()],
});

const EXISTING_SPECIAL_HIGHLIGHT = [
  {
    key: "existing",
    color: "rgba(240, 75, 85, 0.25)",
    borderColor: "rgba(240, 75, 85, 0.5)",
    label: "Existing special hours",
  },
];

const formatLocalIsoDate = (dateObj) =>
  `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;

const AddEditSpecialHoursDialog = ({ open, mode, entry, storeId, existingDates, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = moment(today, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
  const [form, setForm] = React.useState(EMPTY_ENTRY());
  const [errors, setErrors] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
  const originalRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const sortByKey = (arr, key) => [...(arr || [])].sort((a, b) => (a[key] > b[key] ? 1 : -1));
    const initial = entry
      ? {
        ...EMPTY_ENTRY(),
        ...entry,
        valid_on: entry.valid_on || null,
        open_hours: sortByKey(entry.open_hours, "open"),
        quiet_hours: sortByKey(entry.quiet_hours, "start"),
        has_quiet_hours: entry.type === "Closed" ? false : entry.has_quiet_hours,
      }
      : EMPTY_ENTRY();
    setForm(initial);
    setErrors({});
    originalRef.current = JSON.stringify(initial);
  }, [open, entry]);

  const patch = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // Live duplicate-date check: spec requires error on set, not only on save
  React.useEffect(() => {
    if (!open) return;
    if (!form.valid_on) {
      setErrors((prev) => {
        if (!prev.valid_on) return prev;
        const { valid_on, ...rest } = prev;
        return rest;
      });
      return;
    }
    const isDuplicate =
      existingDates.includes(form.valid_on) &&
      !(mode === "edit" && entry?.valid_on === form.valid_on);
    setErrors((prev) => {
      if (isDuplicate) {
        const msg = "A special schedule already exists for this date";
        if (prev.valid_on === msg) return prev;
        return { ...prev, valid_on: msg };
      }
      if (prev.valid_on) {
        const { valid_on, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, [form.valid_on, existingDates, mode, entry, open]);

  const isDirty = () => JSON.stringify(form) !== originalRef.current;

  const validate = () => {
    const errs = {};

    if (!form.valid_on) {
      errs.valid_on = "Valid on is required";
    } else if (form.valid_on <= today) {
      errs.valid_on = "Date cannot be today or in the past";
    } else if (
      existingDates.includes(form.valid_on) &&
      !(mode === "edit" && entry?.valid_on === form.valid_on)
    ) {
      errs.valid_on = "A special schedule already exists for this date";
    }

    if (!form.name) errs.name = "Name is required";

    if (form.type === "Open") {
      // Flag empties first (all rows) so per-row empty errors show regardless of
      // another row being inverted/overlapping.
      if (form.open_hours.some((r) => !r.open || !r.close)) {
        errs.open_hours = "All time ranges require open and close times";
      } else {
        for (const [i, r] of form.open_hours.entries()) {
          if (isInvertedRange(r.open, r.close)) {
            errs.open_hours = "End time must be after start time";
            break;
          }
          if (detectRangeOverlap(form.open_hours, i, "open", "close")) {
            errs.open_hours = "Overlaps with another time range";
            break;
          }
        }
      }
    }

    if (form.has_quiet_hours) {
      if (form.quiet_hours.some((r) => !r.start || !r.end)) {
        errs.quiet_hours = "All quiet hour ranges require start and end times";
      } else {
        for (const [i, r] of form.quiet_hours.entries()) {
          // Treat 00:00 end as midnight (EOD sentinel); only flag a true inverted range
          if (isInvertedRange(r.start, r.end)) {
            errs.quiet_hours = "End time must be after start time";
            break;
          }
          if (detectRangeOverlap(form.quiet_hours, i, "start", "end")) {
            errs.quiet_hours = "Overlaps with another time range";
            break;
          }
        }
      }
    }

    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast("Please fill in valid information", { type: "error" });
      return;
    }

    const sortByKey = (arr, key) => [...arr].sort((a, b) => (a[key] > b[key] ? 1 : -1));

    const open_hours =
      form.type === "Open 24 Hours"
        ? [{ open: "00:00", close: "23:59" }]
        : form.type === "Closed"
          ? []
          : sortByKey(form.open_hours, "open");

    const payload = {
      valid_on: form.valid_on,
      name: form.name,
      type: form.type,
      open_hours,
      quiet_hours:
        form.type !== "Closed" && form.has_quiet_hours
          ? sortByKey(form.quiet_hours, "start")
          : [],
    };

    const result =
      mode === "edit"
        ? await updateSpecialHours(storeId, form.id, payload)
        : await saveSpecialHours(storeId, payload);

    if (result.success) onSaved();
  };

  const handleCancel = () => {
    if (isDirty()) {
      setShowCancelConfirmation(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => e.preventDefault()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {mode === "edit" ? "Edit Special Hours" : "Add Special Hours"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ color: "#62626E", mb: 0.1 }}>
              Each date can have only one special schedule.
            </Typography>
            <Typography variant="body2" sx={{ color: "#62626E", mb: 0.1 }}>
              Special Hours replace the regular opening hours for that day.
            </Typography>
            <Typography variant="body2" sx={{ color: "#62626E" }}>
              Only Quiet Hours defined in the special schedule apply. Regular Quiet Hours are
              ignored.
            </Typography>
          </Box>

          {/* No reserveErrorSpace — this is a solo field (not a Start/End pair),
              and the blank helper line made Valid on → Name taller than the other
              in-section gaps. Errors still render and grow the row when present. */}
          <Box sx={{ mb: 2, maxWidth: 200 }}>
            <DateInputField
              label="Valid on"
              elemId="special_valid_on"
              fieldName="valid_on"
              defaultVal={form.valid_on || ""}
              fieldError={errors.valid_on || ""}
              minSelectable={tomorrow}
              highlightedDates={
                existingDates?.length
                  ? (dateObj) => {
                    const d = formatLocalIsoDate(dateObj);
                    if (mode === "edit" && entry?.valid_on === d) return null;
                    return existingDates.includes(d) ? "existing" : null;
                  }
                  : undefined
              }
              highlightLegend={existingDates?.length ? EXISTING_SPECIAL_HIGHLIGHT : undefined}
              onChangeCall={(date) =>
                patch("valid_on", date ? moment(date).format("YYYY-MM-DD") : null)
              }
            />
          </Box>

          {/* Local override only — TextInputField's margin="normal" adds a large
              top margin that made Valid on → Name look uneven vs the other fields. */}
          <Box sx={{ mb: 2, maxWidth: 200 }}>
            <TextInputField
              label="Name"
              elemId="special_name"
              fieldName="name"
              defaultVal={form.name}
              fieldError={errors.name || ""}
              onChangeCall={(e) => patch("name", e.target.value)}
              sx={{ mt: 0, mb: 0 }}
            />
          </Box>

          <Box sx={{ mb: 2, maxWidth: 300 }}>
            <SingleSelect
              label="Type"
              elemId="special_hours_type"
              enableFullWidth={true}
              options={SPECIAL_HOURS_TYPES.map((t) => ({ label: t }))}
              defaultVal={{ label: form.type }}
              selectionCheck={(option, value) => option.label === value.label}
              onChangeCall={(selectedOption) =>
                setForm((prev) => ({
                  ...prev,
                  type: selectedOption.label,
                  // Closed store can't have Quiet Hours: force-disable so nothing is sent
                  has_quiet_hours:
                    selectedOption.label === "Closed" ? false : prev.has_quiet_hours,
                }))
              }
              sortOptions={false}
              disableSearch={true}
            />
          </Box>

          {/* mb: 2 after Type (above) + this section matches Quiet Hours: equal
              field spacing inside a section, slightly larger gap into the next. */}
          {(form.type === "Open" || form.type === "Open 24 Hours") && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Open Hours
              </Typography>
              {form.type === "Open 24 Hours" ? (
                <Typography variant="body2" sx={{ color: "#62626E" }}>
                  Store is open for 24 hours
                </Typography>
              ) : (
                <>
                  <TimeRangeBlock
                    ranges={form.open_hours}
                    showEmptyErrors={errors.open_hours === "All time ranges require open and close times"}
                    onChange={(ranges) => patch("open_hours", ranges)}
                  />
                </>
              )}
            </Box>
          )}

          {/* Align switch track with fields above (offset MUI small-switch padding).
              No extra mt — Type / Open Hours already end with mb: 2, same gap as
              Type → Quiet Hours section in the Quiet Hours dialog. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: form.has_quiet_hours ? 1 : 2, ml: "-7px" }}
          >
            <HtmlTooltipDisabled
              title={
                form.type === "Closed"
                  ? "Quiet Hours are not available when the store is closed"
                  : ""
              }
            >
              <span>
                <StyledSwitch
                  checked={form.has_quiet_hours}
                  size="small"
                  disabled={form.type === "Closed"}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      has_quiet_hours: checked,
                      quiet_hours:
                        checked && (!prev.quiet_hours || prev.quiet_hours.length === 0)
                          ? [DEFAULT_QUIET_RANGE()]
                          : prev.quiet_hours,
                    }));
                  }}
                />
              </span>
            </HtmlTooltipDisabled>
            <Typography variant="body2">Quiet Hours</Typography>
            <HtmlTooltip
              title={
                <Box width={370}>
                  <Typography variant="body2" sx={{ mb: 0.2 }}>
                    During Quiet Hours, advertising is displayed without sound.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.2 }}>
                    Quiet hours outside opening hours are ignored.
                  </Typography>
                  <Typography variant="body2">
                    The Quiet Hours are only applicable during these special hours.
                  </Typography>
                </Box>
              }
            >
              <Info fontSize="small" sx={{ color: "#62626E", cursor: "pointer" }} />
            </HtmlTooltip>
          </Stack>

          {form.has_quiet_hours && (
            <Box sx={{ mb: 2 }}>
              <TimeRangeBlock
                ranges={form.quiet_hours}
                openKey="start"
                closeKey="end"
                showEmptyErrors={errors.quiet_hours === "All quiet hour ranges require start and end times"}
                onChange={(ranges) => patch("quiet_hours", ranges)}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <CloseButton onClick={handleCancel} label="Cancel" icon={<Close />} />
            <ActionButton onClick={handleSave} label="Save" icon={<Save />} sx={{ mt: 0, mb: 0 }} />
          </Stack>
        </DialogActions>
      </Dialog>

      {showCancelConfirmation && (
        <Confirmation
          showConfirmationPopup={showCancelConfirmation}
          closePopup={() => setShowCancelConfirmation(false)}
          confirmationActionCall={() => {
            setShowCancelConfirmation(false);
            onClose();
          }}
          confirmationMessage="Are you certain you want to discard all the changes you have made?"
          confirmationTitle="Confirmation"
        />
      )}
    </>
  );
};

export default AddEditSpecialHoursDialog;
